/**
 * Composant checkbox pour la prise en charge d'une demande
 */
import React from "react";

interface PriseEnChargeCheckboxProps {
  checked: boolean;
  isUpdating: boolean;
  canToggle: boolean; // Indique si l'utilisateur peut cocher/décocher
  userName?: string; // Nom de l'utilisateur qui a pris en charge
  etatDemande?: number; // État de la demande (1=CREE, 2=VALIDE, 3=EN_COURS, 4=REFUSE)
  messageRefus?: string; // Message d'explication du refus
  onChange: (checked: boolean) => void;
}

const PriseEnChargeCheckbox: React.FC<PriseEnChargeCheckboxProps> = ({
  checked,
  isUpdating,
  canToggle,
  userName,
  etatDemande,
  messageRefus,
  onChange
}) => {
  // Déterminer le badge selon l'état
  const getBadgeConfig = () => {
    if (etatDemande === 2) { // VALIDE
      return {
        color: 'bg-success',
        icon: 'bi-check-circle-fill',
        text: 'Validée'
      };
    } else if (etatDemande === 5) { // REFUSE
      return {
        color: 'bg-danger',
        icon: 'bi-x-circle-fill',
        text: 'Refusée'
      };
    } else if (etatDemande === 4) { // AFFECTATION_MARQUES
      return {
        color: 'bg-warning',
        icon: 'bi-tags-fill',
        text: 'Affectation marques'
      };
    } else if (checked) { // EN_COURS
      return {
        color: 'bg-primary',
        icon: 'bi-person-check-fill',
        text: 'Pris en charge'
      };
    } else { // CREE ou par défaut
      return {
        color: 'bg-secondary',
        icon: 'bi-person-dash',
        text: 'Disponible'
      };
    }
  };

  // Déterminer le titre selon l'état
  const getTitle = () => {
    if (etatDemande === 2) {
      return 'Demande validée';
    }
    if (etatDemande === 5) {
      return 'Demande refusée';
    }
    if (etatDemande === 4) {
      return 'Affectation des marques en cours';
    }
    if (checked) {
      return 'Demande en cours de traitement';
    }
    return 'Prendre en charge cette demande';
  };

  // Déterminer la description selon l'état
  const getDescription = () => {
    if (etatDemande === 2 && userName) {
      return `Validé par : ${userName}`;
    }
    if (etatDemande === 4 && userName) {
      return `Affectation des marques par : ${userName}`;
    }
    if (etatDemande === 5) {
      if (userName && messageRefus) {
        return (
          <>
            Refusé par : {userName}
            <br />
            <strong>Motif :</strong> {messageRefus}
          </>
        );
      }
      if (userName) {
        return `Refusé par : ${userName}`;
      }
      if (messageRefus) {
        return (
          <>
            <strong>Motif du refus :</strong> {messageRefus}
          </>
        );
      }
    }
    if (checked && userName) {
      return `Pris en charge par : ${userName}`;
    }
    if (checked) {
      return 'Vous avez pris en charge cette demande';
    }
    return 'Attribuez-vous cette demande pour commencer le traitement';
  };

  const badgeConfig = getBadgeConfig();

  return (
    <div className="row align-items-center">
      <div className="col-md-8">
        <div className="d-flex align-items-center">
          <div 
            className={`badge ${badgeConfig.color} me-3`} 
            style={{ fontSize: '1rem', padding: '0.75rem 1rem' }}
          >
            <i className={`bi ${badgeConfig.icon} me-2`}></i>
            {badgeConfig.text}
          </div>
          <div>
            <h6 className="mb-1" style={{ fontWeight: 600 }}>
              {getTitle()}
            </h6>
            <small className="text-muted">
              {getDescription()}
            </small>
          </div>
        </div>
      </div>
      <div className="col-md-4 text-end">
        {/* Ne pas afficher le bouton si la demande est validée, refusée ou en affectation marques */}
        {etatDemande !== 2 && etatDemande !== 4 && etatDemande !== 5 && (
          <button
            className={`btn ${checked ? 'btn-outline-success' : 'btn-primary'} px-4`}
            onClick={() => onChange(!checked)}
            disabled={isUpdating || !canToggle}
            style={{ 
              minWidth: '180px',
              fontWeight: 500,
              transition: 'all 0.3s ease'
            }}
          >
            {isUpdating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Traitement...
              </>
            ) : (
              <>
                <i className={`bi ${checked ? 'bi-check-circle-fill' : 'bi-hand-thumbs-up'} me-2`}></i>
                {checked ? 'En charge' : 'Prendre en charge'}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default PriseEnChargeCheckbox;
